import { Check } from "lucide-react";
import { Seo } from "../components/Seo";
import { Eyebrow, GhostLink, Reveal } from "../components/ui-bits";
import { CTABand, HubCard, SectionHeading } from "../components/PageSections";
import { TOWNS } from "../data/site";

const PROMISES = [
  "Our own shop van and professional driver — never a relay service",
  "Same-day delivery in most cases when you order before 3:00 p.m.",
  "Every delivery hand-carried to the door and confirmed",
  "Flowers travel minutes, not days — from our cooler to their door",
];

export default function Delivery() {
  return (
    <>
      <Seo
        title="Same-Day Flower Delivery Kent County RI | JIF Flowers"
        description="Same-day flower delivery across Kent County, RI — Coventry, West Warwick, Warwick, East Greenwich & West Greenwich. Call JIF: (401) 822-4440."
      />
      <section className="mx-auto max-w-7xl px-5 pb-16 pt-40 sm:px-8">
        <Reveal>
          <Eyebrow>Flower Delivery</Eyebrow>
          <h1 className="mt-5 max-w-3xl font-serif text-4xl font-medium leading-[1.08] tracking-tight text-plum sm:text-5xl lg:text-6xl">
            Hand-Delivered Across Kent County
          </h1>
          <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-charcoal/85 sm:text-[18px]">
            We deliver throughout Coventry, RI and the Kent County area with our own shop van and professional
            driver. Every delivery is hand-carried and confirmed — never boxed, never shipped, never handed to a
            stranger.
          </p>
        </Reveal>
      </section>

      <section className="bg-sagetint">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
            <Reveal>
              <Eyebrow>Our Promise</Eyebrow>
              <h2 className="mt-4 font-serif text-3xl font-medium leading-snug text-plum sm:text-4xl">
                From our cooler to their door, the same day
              </h2>
              <ul className="mt-8 space-y-4">
                {PROMISES.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-[16px] text-charcoal/90">
                    <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-plum/10">
                      <Check className="h-3.5 w-3.5 text-plum" strokeWidth={3} aria-hidden="true" />
                    </span>
                    {p}
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <GhostLink href="/hospital-flower-delivery" testid="delivery-hospital-link">
                  Hospital & Nursing Home Delivery
                </GhostLink>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="relative">
                <div className="absolute -left-4 -top-4 h-full w-full rounded-xl border border-gold/50" aria-hidden="true" />
                <img
                  src="https://images.pexels.com/photos/34041965/pexels-photo-34041965.jpeg?auto=compress&cs=tinysrgb&w=1200"
                  alt="Fresh arrangements ready for delivery at JIF Flowers, Coventry"
                  loading="lazy"
                  className="relative aspect-[4/5] w-full rounded-xl object-cover shadow-xl shadow-sage/15"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <SectionHeading
          eyebrow="Delivery Area"
          title="Towns we deliver to every day"
          lede="Choose your town for local details — or just call the shop and tell us where the flowers are going."
        />
        <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {TOWNS.map((t) => (
            <HubCard
              key={t.path}
              href={t.path}
              image="https://images.unsplash.com/photo-1572454591674-2739f30d8c40?crop=entropy&cs=srgb&fm=jpg&q=85"
              imageAlt={`Flower delivery in ${t.name}, RI by JIF Flowers`}
              eyebrow="Flower Delivery"
              title={`${t.name}, RI`}
              blurb={t.lede}
              testid={`town-card-${t.slug}`}
            />
          ))}
        </div>
      </section>

      <CTABand title="Order before 3:00 p.m. for same-day delivery in most cases" />
    </>
  );
}
